import { defineComponent as _defineComponent } from 'vue'
import FormLayout from '@/components/common/FormLayout.vue'
import { useStore } from 'vuex-composition-helpers'
import { computed, reactive, ref, watchEffect } from 'vue'
import { OFFLINE, STANDBY } from '@/models/unit_number'
import { useResourceQuery } from '@/api/unit_numbers'
import { useRouter } from 'vue-router/composables'
import VsForm from '@/components/vision/VsForm.vue'
import {
  useUpdateUnitNumberMutation,
  useArchiveUnitNumberMutation,
  useUnarchiveUnitNumberMutation,
} from '@/api/account-data/manage-unit-numbers.js'
import TwoStageButton from '@/components/common/TwoStageButton.vue'
import {
  useGetResourceTypeOptions,
  useGetDivisionOptions,
  useGetResourceOwnerOptions,
} from '@/api/options'
import { useQueryClient } from '@tanstack/vue-query'

interface FormData {
  name: string | null
  description: string | null
  statusDescription: string | null
  resourceTypeId: number | null
  ownerId: string | null
  divisionId: number | null
  status: number | null
  isArchived: boolean | null
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ResourceForm',
  props: {
    id: null,
    dismissTo: { default: null }
  },
  emits: ['dismiss', 'save'],
  setup(__props: any, { emit }) {

const props = __props as { id: number | string, dismissTo: { name: string } | null }


const store = useStore()
const router = useRouter()


const queryClient = useQueryClient()

const isSaving = ref(false)
const form = ref<InstanceType<typeof VsForm>>()
const formData = reactive<FormData>({
  name: null,
  description: null,
  statusDescription: null,
  resourceTypeId: null,
  ownerId: null,
  divisionId: null,
  status: null,
  isArchived: null,
})

const idRef = computed(() => Number(props.id))
const { data: resource, isLoading, refetch } = useResourceQuery(idRef)

const { data: resourceTypeOptions } = useGetResourceTypeOptions()
const { data: resourceOwnerOptions } = useGetResourceOwnerOptions()
const { data: divisionOptions } = useGetDivisionOptions()

const {
  error: updateError,
  isError: isUpdateError,
  mutateAsync: updateResource,
} = useUpdateUnitNumberMutation()
const { isLoading: isArchiving, mutateAsync: archiveResource } =
  useArchiveUnitNumberMutation()
const { isLoading: isUnarchiving, mutateAsync: unarchiveResource } =
  useUnarchiveUnitNumberMutation()

watchEffect(() => {
  if (!isLoading.value && resource.value) {
    // Initial loading - Only run once
    // isLoading will only change from true to false once, at the beginning
    formData.name = resource.value.name
    formData.description = resource.value.description
    formData.statusDescription = resource.value.statusDescription
    formData.resourceTypeId = resource.value.resourceTypeId
    formData.ownerId = resource.value.ownerId
    formData.divisionId = resource.value.divisionId
    formData.status = resource.value.status
    formData.isArchived = !resource.value.isVisible
  }
})

const hasChanges = computed(() => {
  return (
    formData.name !== resource.value?.name ||
    formData.description !== resource.value?.description ||
    formData.statusDescription !== resource.value?.statusDescription ||
    formData.resourceTypeId !== resource.value?.resourceTypeId ||
    formData.ownerId !== resource.value?.ownerId ||
    formData.divisionId !== resource.value?.divisionId ||
    formData.status !== resource.value?.status
  )
})

const title = computed(() => {
  if (resource.value && resource.value.id) {
    return `Edit ${resource.value.name}`
  }
  return null
})

const statusItems = ref([
  { text: 'Online', value: STANDBY },
  { text: 'Offline', value: OFFLINE },
])
const status = computed(() => (formData.status === OFFLINE ? OFFLINE : STANDBY))
const archiveLabel = computed(() => (formData.isArchived ? 'Archived' : ''))

// Vuex
const canFullyModifyResource = computed<boolean>(
  () => store.getters['permissions/canFullyModifyResource']
)

function handleUpdateForm(field: string, e: any) {
  if (field === 'statusDescription') {
    formData[field] = e || ''
  }
  formData[field] = e
}

function dismiss() {
  if (hasChanges.value) {
    const confirmation = window.confirm(
      'There are unsaved changes, are you sure you want to discard them?'
    )

    if (!confirmation) {
      return
    }
  }

  if (props.dismissTo) {
    router.push(props.dismissTo)
  } else {
    emit('dismiss')
  }
}

async function submit() {
  isSaving.value = true
  form.value?.isValid() && (await update())
  isSaving.value = false
}

async function update() {
  try {
    await updateResource({
      id: props.id,
      resourceTypeId: formData.resourceTypeId,
      name: formData.name,
      divisionId: formData.divisionId,
      status: formData.status,
      statusDescription: formData.statusDescription,
      description: formData.description,
      isVisible: !formData.isArchived,
      ownerId: formData.ownerId,
    })

    queryClient.invalidateQueries(['resource', props.id])
  } catch (e) {
    return
  }

  if (!isUpdateError.value) {
    await refetch()
    emit('save')
  }
}

async function handleArchive() {
  const confirmation = window.confirm(
    'Are you sure you want to archive this resource?'
  )
  if (confirmation && resource.value) {
    try {
      if (formData.isArchived) {
        await unarchiveResource(resource.value)
      } else {
        await archiveResource(resource.value)
      }
      formData.isArchived = !formData.isArchived
      await refetch()
    } catch (e) {}
    emit('save')
  }
}

return { __sfc: true,props, store, router, emit, queryClient, isSaving, form, formData, idRef, resource, isLoading, refetch, resourceTypeOptions, resourceOwnerOptions, divisionOptions, updateError, isUpdateError, updateResource, isArchiving, archiveResource, isUnarchiving, unarchiveResource, hasChanges, title, statusItems, status, archiveLabel, canFullyModifyResource, handleUpdateForm, dismiss, submit, update, handleArchive, FormLayout, VsForm, TwoStageButton }
}

})